import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import {
  DollarSign, TrendingUp, TrendingDown, Calendar, PieChart, BarChart3,
  Users, FileText, AlertCircle, CheckCircle, ArrowUp, ArrowDown,
  Download, Filter, RefreshCw, Briefcase, Clock, Target, Receipt
} from 'lucide-react';
import { useFeatures } from './FeatureContext';
import { useTheme } from './ThemeContext';

// Brand-aligned semantic palette — replaces the old flat-UI hex literals
// (#4ecca3, #f39c12, #e74c3c, #3498db, #9b59b6, #1abc9c) used throughout.
const FIN = {
  positive: '#10B981',  // revenue, profit, growth
  negative: '#EF4444',  // expenses, overdue, decline
  warning:  '#F59E0B',  // outstanding, in-progress
  info:     '#3B82F6',  // scheduled, neutral metric
  teal:     '#028090',  // brand accent for tertiary highlights
  purple:   '#8B5CF6',  // for diversity in chart series only
};

export default function FinancialDashboard({ companyId }) {
  const { hasFeature, tierName } = useFeatures();
  const { colors } = useTheme();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const exportBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    width: '100%',
    padding: '0.75rem',
    background: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: colors.text,
    cursor: 'pointer',
    fontSize: '0.9rem',
    textAlign: 'left'
  };

  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month'); // 'week', 'month', 'quarter', 'year', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  // Real data state
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [expenses, setExpenses] = useState([]);

  // Calculated metrics
  const [metrics, setMetrics] = useState({
    revenue: { current: 0, previous: 0, ytd: 0 },
    outstanding: { total: 0, overdue: 0, unpaidCount: 0 },
    jobs: { completed: 0, inProgress: 0, scheduled: 0, cancelled: 0 },
    quotes: { sent: 0, accepted: 0, declined: 0, conversionRate: 0 },
    avgJobValue: 0,
    avgDaysToClose: 0,
    topCustomers: [],
    revenueByMonth: [],
    revenueBySource: [],
    revenueByTech: [],
    jobsByType: []
  });

  useEffect(() => {
    if (companyId) {
      loadAllData();
    } else {
      setLoading(false);
    }
  }, [companyId, dateRange, customStartDate, customEndDate]);

  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate, previousStart, previousEnd;

    endDate = new Date(now);

    switch (dateRange) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        previousStart = new Date(startDate);
        previousStart.setDate(previousStart.getDate() - 7);
        previousEnd = new Date(startDate);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        previousStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        previousEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        previousStart = new Date(now.getFullYear(), (quarter - 1) * 3, 1);
        previousEnd = new Date(now.getFullYear(), quarter * 3, 0);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        previousStart = new Date(now.getFullYear() - 1, 0, 1);
        previousEnd = new Date(now.getFullYear() - 1, 11, 31);
        break;
      case 'custom':
        startDate = customStartDate ? new Date(customStartDate) : new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = customEndDate ? new Date(customEndDate) : now;
        const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);
        previousEnd = new Date(startDate);
        previousEnd.setDate(previousEnd.getDate() - 1);
        previousStart = new Date(previousEnd);
        previousStart.setDate(previousStart.getDate() - daysDiff);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        previousStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        previousEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    }

    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0],
      previousStart: previousStart.toISOString().split('T')[0],
      previousEnd: previousEnd.toISOString().split('T')[0]
    };
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const dates = getDateRange();
      const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];

      // Load invoices
      const { data: invoiceData } = await supabaseData
        .from('invoices')
        .select(`
          *,
          customers (id, first_name, last_name, name)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      // Load jobs
      const { data: jobData } = await supabaseData
        .from('jobs')
        .select(`
          *,
          customers (id, first_name, last_name, name, lead_source)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      // Load quotes
      const { data: quoteData } = await supabaseData
        .from('quotes')
        .select(`
          *,
          customers (id, first_name, last_name, name)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      // Load customers
      const { data: customerData } = await supabaseData
        .from('customers')
        .select('*')
        .eq('company_id', companyId);

      // Load team members (separate queries to avoid relationship issues)
      const { data: teamData } = await supabaseData
        .from('user_companies')
        .select('*')
        .eq('company_id', companyId);

      // Get user profiles separately
      const userIds = (teamData || []).map(m => m.user_id);
      let profilesMap = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabaseData
          .from('user_profiles')
          .select('id, full_name, email')
          .in('id', userIds);
        (profiles || []).forEach(p => { profilesMap[p.id] = p; });
      }

      // Merge profiles into team data
      const teamWithProfiles = (teamData || []).map(row => ({
        ...row,
        user_profiles: profilesMap[row.user_id] || null
      }));

      // Load expenses
      const { data: expenseData } = await supabaseData
        .from('expenses')
        .select('*')
        .eq('company_id', companyId)
        .order('expense_date', { ascending: false });

      setInvoices(invoiceData || []);
      setJobs(jobData || []);
      setQuotes(quoteData || []);
      setCustomers(customerData || []);
      setTeamMembers(teamWithProfiles);
      setExpenses(expenseData || []);

      // Calculate metrics
      calculateMetrics(invoiceData || [], jobData || [], quoteData || [], dates, yearStart);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (invoices, jobs, quotes, dates, yearStart) => {
    // Filter by date range
    const currentInvoices = invoices.filter(inv => {
      const date = inv.created_at?.split('T')[0];
      return date >= dates.start && date <= dates.end;
    });

    const previousInvoices = invoices.filter(inv => {
      const date = inv.created_at?.split('T')[0];
      return date >= dates.previousStart && date <= dates.previousEnd;
    });

    const ytdInvoices = invoices.filter(inv => {
      const date = inv.created_at?.split('T')[0];
      return date >= yearStart;
    });

    const currentJobs = jobs.filter(job => {
      const date = (job.completed_at || job.created_at)?.split('T')[0];
      return date >= dates.start && date <= dates.end;
    });

    const currentQuotes = quotes.filter(q => {
      const date = q.created_at?.split('T')[0];
      return date >= dates.start && date <= dates.end;
    });

    // Revenue calculations
    const currentRevenue = currentInvoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + (parseFloat(inv.amount_paid) || parseFloat(inv.total) || 0), 0);

    const previousRevenue = previousInvoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + (parseFloat(inv.amount_paid) || parseFloat(inv.total) || 0), 0);

    const ytdRevenue = ytdInvoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + (parseFloat(inv.amount_paid) || parseFloat(inv.total) || 0), 0);

    // Outstanding calculations
    const outstandingInvoices = invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled');
    const totalOutstanding = outstandingInvoices.reduce((sum, inv) => {
      const total = parseFloat(inv.total) || 0;
      const paid = parseFloat(inv.amount_paid) || 0;
      return sum + (total - paid);
    }, 0);

    const overdueInvoices = outstandingInvoices.filter(inv => {
      if (!inv.due_date) return false;
      return new Date(inv.due_date) < new Date();
    });
    const overdueAmount = overdueInvoices.reduce((sum, inv) => {
      const total = parseFloat(inv.total) || 0;
      const paid = parseFloat(inv.amount_paid) || 0;
      return sum + (total - paid);
    }, 0);

    // Job metrics
    const completedJobs = currentJobs.filter(j => j.status === 'completed').length;
    const inProgressJobs = jobs.filter(j => j.status === 'in-progress').length;
    const scheduledJobs = jobs.filter(j => j.status === 'scheduled').length;
    const cancelledJobs = currentJobs.filter(j => j.status === 'cancelled').length;

    // Quote metrics
    const sentQuotes = currentQuotes.length;
    const acceptedQuotes = currentQuotes.filter(q => q.status === 'accepted' || q.status === 'signed').length;
    const declinedQuotes = currentQuotes.filter(q => q.status === 'declined' || q.status === 'expired').length;
    const conversionRate = sentQuotes > 0 ? (acceptedQuotes / sentQuotes * 100) : 0;

    // Avg job value
    const paidInvoices = currentInvoices.filter(inv => inv.status === 'paid');
    const avgJobValue = paidInvoices.length > 0
      ? paidInvoices.reduce((sum, inv) => sum + (parseFloat(inv.total) || 0), 0) / paidInvoices.length
      : 0;

    // Top customers (by revenue)
    const customerRevenue = {};
    invoices.filter(inv => inv.status === 'paid').forEach(inv => {
      const customerId = inv.customer_id;
      const customerName = inv.customers
        ? (inv.customers.first_name && inv.customers.last_name
            ? `${inv.customers.first_name} ${inv.customers.last_name}`
            : inv.customers.name || 'Unknown')
        : 'Unknown';
      if (!customerRevenue[customerId]) {
        customerRevenue[customerId] = { name: customerName, revenue: 0, jobs: 0 };
      }
      customerRevenue[customerId].revenue += parseFloat(inv.total) || 0;
      customerRevenue[customerId].jobs += 1;
    });
    const topCustomers = Object.values(customerRevenue)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    // Revenue by month (last 6 months)
    const revenueByMonth = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      const monthRevenue = invoices
        .filter(inv => {
          if (inv.status !== 'paid') return false;
          const invDate = new Date(inv.created_at);
          return invDate >= monthStart && invDate <= monthEnd;
        })
        .reduce((sum, inv) => sum + (parseFloat(inv.total) || 0), 0);

      revenueByMonth.push({
        month: monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        revenue: monthRevenue,
        isCurrent: i === 0
      });
    }

    // Revenue by lead source
    const sourceRevenue = {};
    jobs.filter(j => j.status === 'completed').forEach(job => {
      const source = job.customers?.lead_source || job.lead_source || 'Direct';
      if (!sourceRevenue[source]) sourceRevenue[source] = 0;
      sourceRevenue[source] += parseFloat(job.quoted_value) || 0;
    });
    const totalSourceRevenue = Object.values(sourceRevenue).reduce((a, b) => a + b, 0);
    const revenueBySource = Object.entries(sourceRevenue)
      .map(([source, revenue]) => ({
        source,
        revenue,
        percentage: totalSourceRevenue > 0 ? (revenue / totalSourceRevenue * 100) : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    // Revenue by tech
    const techRevenue = {};
    jobs.filter(j => j.status === 'completed').forEach(job => {
      const tech = job.assigned_tech || 'Unassigned';
      if (!techRevenue[tech]) techRevenue[tech] = { revenue: 0, jobs: 0 };
      techRevenue[tech].revenue += parseFloat(job.quoted_value) || 0;
      techRevenue[tech].jobs += 1;
    });
    const revenueByTech = Object.entries(techRevenue)
      .map(([tech, data]) => ({ tech, ...data }))
      .sort((a, b) => b.revenue - a.revenue);

    // Jobs by type
    const typeCount = {};
    jobs.forEach(job => {
      const type = job.job_type || 'other';
      if (!typeCount[type]) typeCount[type] = 0;
      typeCount[type]++;
    });
    const jobsByType = Object.entries(typeCount)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);

    setMetrics({
      revenue: { current: currentRevenue, previous: previousRevenue, ytd: ytdRevenue },
      outstanding: { total: totalOutstanding, overdue: overdueAmount, unpaidCount: outstandingInvoices.length },
      jobs: { completed: completedJobs, inProgress: inProgressJobs, scheduled: scheduledJobs, cancelled: cancelledJobs },
      quotes: { sent: sentQuotes, accepted: acceptedQuotes, declined: declinedQuotes, conversionRate },
      avgJobValue,
      avgDaysToClose: 0,
      topCustomers,
      revenueByMonth,
      revenueBySource,
      revenueByTech,
      jobsByType
    });
  };

  // CSV Export Functions
  const exportToCSV = (type) => {
    setExporting(true);
    let csvContent = '';
    let filename = '';
    const dates = getDateRange();

    switch (type) {
      case 'invoices':
        filename = `invoices_${dates.start}_to_${dates.end}.csv`;
        csvContent = generateInvoiceCSV();
        break;
      case 'revenue':
        filename = `revenue_summary_${dates.start}_to_${dates.end}.csv`;
        csvContent = generateRevenueCSV();
        break;
      case 'jobs':
        filename = `jobs_${dates.start}_to_${dates.end}.csv`;
        csvContent = generateJobsCSV();
        break;
      case 'customers':
        filename = `customers_${new Date().toISOString().split('T')[0]}.csv`;
        csvContent = generateCustomersCSV();
        break;
      case 'qbo':
        filename = `quickbooks_import_${dates.start}_to_${dates.end}.csv`;
        csvContent = generateQBOCSV();
        break;
      default:
        break;
    }

    downloadCSV(csvContent, filename);
    setExporting(false);
  };

  const generateInvoiceCSV = () => {
    const headers = ['Invoice #', 'Date', 'Due Date', 'Customer', 'Description', 'Subtotal', 'Tax', 'Total', 'Amount Paid', 'Balance', 'Status'];
    const rows = invoices.map(inv => {
      const customerName = inv.customers
        ? (inv.customers.first_name && inv.customers.last_name
            ? `${inv.customers.first_name} ${inv.customers.last_name}`
            : inv.customers.name || '')
        : '';
      const balance = (parseFloat(inv.total) || 0) - (parseFloat(inv.amount_paid) || 0);
      return [
        inv.invoice_number || '',
        inv.created_at?.split('T')[0] || '',
        inv.due_date || '',
        `"${customerName}"`,
        `"${(inv.description || '').replace(/"/g, '""')}"`,
        (parseFloat(inv.subtotal) || 0).toFixed(2),
        (parseFloat(inv.tax_amount) || 0).toFixed(2),
        (parseFloat(inv.total) || 0).toFixed(2),
        (parseFloat(inv.amount_paid) || 0).toFixed(2),
        balance.toFixed(2),
        inv.status || ''
      ].join(',');
    });
    return [headers.join(','), ...rows].join('\n');
  };

  const generateRevenueCSV = () => {
    const headers = ['Period', 'Revenue', 'Jobs Completed', 'Avg Job Value', 'Outstanding', 'Overdue'];
    const rows = [
      ['Current Period', metrics.revenue.current.toFixed(2), metrics.jobs.completed, metrics.avgJobValue.toFixed(2), metrics.outstanding.total.toFixed(2), metrics.outstanding.overdue.toFixed(2)],
      ['Year to Date', metrics.revenue.ytd.toFixed(2), '', '', '', '']
    ];
    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  };

  const generateJobsCSV = () => {
    const headers = ['Job #', 'Date', 'Customer', 'Type', 'Status', 'Quoted Value', 'Assigned Tech', 'Lead Source'];
    const rows = jobs.map(job => {
      const customerName = job.customers
        ? (job.customers.first_name && job.customers.last_name
            ? `${job.customers.first_name} ${job.customers.last_name}`
            : job.customers.name || '')
        : '';
      return [
        job.job_number || '',
        job.created_at?.split('T')[0] || '',
        `"${customerName}"`,
        job.job_type || '',
        job.status || '',
        (parseFloat(job.quoted_value) || 0).toFixed(2),
        job.assigned_tech || '',
        job.customers?.lead_source || job.lead_source || ''
      ].join(',');
    });
    return [headers.join(','), ...rows].join('\n');
  };

  const generateCustomersCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Address', 'City', 'State', 'ZIP', 'Lead Source', 'Created'];
    const rows = customers.map(c => {
      const name = c.first_name && c.last_name ? `${c.first_name} ${c.last_name}` : c.name || '';
      return [
        `"${name}"`,
        c.email || '',
        c.phone || '',
        `"${(c.address || '').replace(/"/g, '""')}"`,
        c.city || '',
        c.state || '',
        c.zip || '',
        c.lead_source || '',
        c.created_at?.split('T')[0] || ''
      ].join(',');
    });
    return [headers.join(','), ...rows].join('\n');
  };

  // QuickBooks-compatible CSV format
  const generateQBOCSV = () => {
    const headers = ['*InvoiceNo', '*Customer', 'InvoiceDate', '*DueDate', 'Terms', 'Location', 'Memo', 'Item(Product/Service)', 'ItemDescription', 'ItemQuantity', 'ItemRate', '*ItemAmount', 'ItemTaxCode', 'ItemTaxAmount', 'ServiceDate'];
    const rows = invoices
      .filter(inv => inv.status === 'paid')
      .map(inv => {
        const customerName = inv.customers
          ? (inv.customers.first_name && inv.customers.last_name
              ? `${inv.customers.first_name} ${inv.customers.last_name}`
              : inv.customers.name || 'Customer')
          : 'Customer';
        return [
          inv.invoice_number || '',
          `"${customerName}"`,
          inv.created_at?.split('T')[0] || '',
          inv.due_date || '',
          'Net 14',
          '',
          `"${(inv.description || '').replace(/"/g, '""')}"`,
          'Service',
          `"${(inv.description || 'Service').replace(/"/g, '""')}"`,
          '1',
          (parseFloat(inv.subtotal) || parseFloat(inv.total) || 0).toFixed(2),
          (parseFloat(inv.subtotal) || parseFloat(inv.total) || 0).toFixed(2),
          'TAX',
          (parseFloat(inv.tax_amount) || 0).toFixed(2),
          inv.created_at?.split('T')[0] || ''
        ].join(',');
      });
    return [headers.join(','), ...rows].join('\n');
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateChange = (current, previous) => {
    if (previous === 0) return { percent: current > 0 ? 100 : 0, direction: current > 0 ? 'up' : 'neutral' };
    const change = ((current - previous) / previous) * 100;
    return {
      percent: Math.abs(change).toFixed(1),
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral'
    };
  };

  const revenueChange = calculateChange(metrics.revenue.current, metrics.revenue.previous);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bgCard,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.textMuted
      }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '0.5rem' }} />
        Loading dashboard...
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      color: colors.text
    }}>
      {/* Header — clean, no gradient slab */}
      <div style={{
        background: colors.bgCard,
        padding: isMobile ? '1rem' : '1.25rem 2rem',
        borderBottom: `1px solid ${colors.border}`
      }}>
        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap',
          gap: isMobile ? '0.75rem' : '1rem'
        }}>
          <div>
            <h1 style={{
              fontSize: isMobile ? '1.35rem' : '1.5rem',
              fontWeight: '700',
              color: colors.text,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem'
            }}>
              <BarChart3 size={isMobile ? 22 : 24} color={colors.accent} />
              Reports &amp; Analytics
            </h1>
            <p style={{ color: colors.textMuted, margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
              {tierName} Plan
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
            {/* Date Range Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: isMobile ? 1 : 'unset' }}>
              <Filter size={16} color={colors.textMuted} />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                style={{
                  flex: isMobile ? 1 : 'unset',
                  padding: '0.5rem 0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  color: colors.text,
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                <option value="week">Last 7 Days</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {dateRange === 'custom' && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    background: colors.bgCard,
                    border: 'none',
                    borderRadius: '6px',
                    color: colors.text
                  }}
                />
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    background: colors.bgCard,
                    border: 'none',
                    borderRadius: '6px',
                    color: colors.text
                  }}
                />
              </div>
            )}

            {/* Export Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => document.getElementById('exportMenu').classList.toggle('show')}
                style={{
                  padding: '0.5rem 1rem',
                  background: colors.bgCard,
                  border: 'none',
                  borderRadius: '6px',
                  color: colors.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.9rem'
                }}
              >
                <Download size={16} />
                Export
              </button>
              <div
                id="exportMenu"
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '0.5rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  padding: '0.5rem',
                  display: 'none',
                  minWidth: '200px',
                  zIndex: 100
                }}
                className="export-menu"
              >
                <button onClick={() => exportToCSV('invoices')} style={exportBtnStyle}>
                  <Receipt size={16} /> Invoices CSV
                </button>
                <button onClick={() => exportToCSV('jobs')} style={exportBtnStyle}>
                  <Briefcase size={16} /> Jobs CSV
                </button>
                <button onClick={() => exportToCSV('customers')} style={exportBtnStyle}>
                  <Users size={16} /> Customers CSV
                </button>
                <button onClick={() => exportToCSV('revenue')} style={exportBtnStyle}>
                  <TrendingUp size={16} /> Revenue Summary
                </button>
                <hr style={{ border: 'none', borderTop: `1px solid ${colors.border}`, margin: '0.5rem 0' }} />
                <button onClick={() => exportToCSV('qbo')} style={{ ...exportBtnStyle, color: FIN.positive }}>
                  <FileText size={16} /> QuickBooks Import
                </button>
              </div>
            </div>

            <button
              onClick={loadAllData}
              style={{
                padding: '0.5rem',
                background: colors.bgCard,
                border: 'none',
                borderRadius: '6px',
                color: colors.text,
                cursor: 'pointer'
              }}
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .export-menu.show { display: block !important; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {/* Main Content */}
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: isMobile ? '1rem' : '2rem' }}>

        {/* Key Metrics Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: isMobile ? '0.75rem' : '1.25rem',
          marginBottom: isMobile ? '1.25rem' : '2rem'
        }}>
          {/* Revenue */}
          <MetricCard
            icon={<DollarSign size={24} color={FIN.positive} />}
            label="Revenue"
            value={formatCurrency(metrics.revenue.current)}
            subValue={`YTD: ${formatCurrency(metrics.revenue.ytd)}`}
            change={revenueChange}
            color={FIN.positive}
            isMobile={isMobile}
          />

          {/* Outstanding */}
          <MetricCard
            icon={<AlertCircle size={24} color={FIN.warning} />}
            label="Outstanding"
            value={formatCurrency(metrics.outstanding.total)}
            subValue={`${metrics.outstanding.unpaidCount} invoices`}
            badge={metrics.outstanding.overdue > 0 ? `${formatCurrency(metrics.outstanding.overdue)} overdue` : null}
            badgeColor={FIN.negative}
            color={FIN.warning}
            isMobile={isMobile}
          />

          {/* Jobs Completed */}
          <MetricCard
            icon={<CheckCircle size={24} color={FIN.info} />}
            label="Jobs Completed"
            value={metrics.jobs.completed}
            subValue={`${metrics.jobs.scheduled} scheduled`}
            color={FIN.info}
            isMobile={isMobile}
          />

          {/* Quote Conversion */}
          <MetricCard
            icon={<Target size={24} color={FIN.purple} />}
            label="Quote Conversion"
            value={`${metrics.quotes.conversionRate.toFixed(1)}%`}
            subValue={`${metrics.quotes.accepted}/${metrics.quotes.sent} accepted`}
            color={FIN.purple}
            isMobile={isMobile}
          />

          {/* Avg Job Value */}
          <MetricCard
            icon={<Receipt size={24} color={FIN.teal} />}
            label="Avg Job Value"
            value={formatCurrency(metrics.avgJobValue)}
            subValue="per completed job"
            color={FIN.teal}
            isMobile={isMobile}
          />
        </div>

        {/* Aging Receivables */}
        {(() => {
          const unpaidInvoices = invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled');
          const now = new Date();
          const buckets = {
            current: { label: 'Current (0-30)', invoices: [], total: 0, color: FIN.positive },
            '30-60': { label: '30-60 Days', invoices: [], total: 0, color: FIN.warning },
            '60-90': { label: '60-90 Days', invoices: [], total: 0, color: '#F97316' },
            '90+': { label: '90+ Days', invoices: [], total: 0, color: FIN.negative }
          };

          unpaidInvoices.forEach(inv => {
            const created = new Date(inv.created_at);
            const daysOld = Math.floor((now - created) / (1000 * 60 * 60 * 24));
            const balance = (parseFloat(inv.total) || 0) - (parseFloat(inv.amount_paid) || 0);
            if (balance <= 0) return;

            let bucket;
            if (daysOld <= 30) bucket = 'current';
            else if (daysOld <= 60) bucket = '30-60';
            else if (daysOld <= 90) bucket = '60-90';
            else bucket = '90+';

            buckets[bucket].invoices.push({ ...inv, balance, daysOld });
            buckets[bucket].total += balance;
          });

          const grandTotal = Object.values(buckets).reduce((s, b) => s + b.total, 0);

          return grandTotal > 0 ? (
            <div style={{
              background: colors.bgCard,
              padding: '1.5rem',
              borderRadius: '12px',
              border: `1px solid ${colors.border}`,
              marginBottom: '2rem'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={20} color={colors.accent} />
                Aging Receivables
                <span style={{ fontSize: '0.8rem', color: colors.textMuted, fontWeight: '400', marginLeft: 'auto' }}>
                  Total: {formatCurrency(grandTotal)}
                </span>
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                {Object.entries(buckets).map(([key, bucket]) => (
                  <div
                    key={key}
                    onClick={() => {
                      const el = document.getElementById(`aging-${key}`);
                      if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                    }}
                    style={{
                      padding: '1rem',
                      background: colors.bg,
                      borderRadius: '8px',
                      borderLeft: `4px solid ${bucket.color}`,
                      cursor: bucket.invoices.length > 0 ? 'pointer' : 'default',
                      opacity: bucket.invoices.length === 0 ? 0.5 : 1
                    }}
                  >
                    <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginBottom: '0.25rem' }}>{bucket.label}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: bucket.color }}>
                      {formatCurrency(bucket.total)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                      {bucket.invoices.length} invoice{bucket.invoices.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>

              {/* Expandable invoice lists */}
              {Object.entries(buckets).map(([key, bucket]) => (
                bucket.invoices.length > 0 && (
                  <div key={`list-${key}`} id={`aging-${key}`} style={{ display: 'none', marginBottom: '0.5rem' }}>
                    <div style={{ fontSize: '0.85rem', color: bucket.color, fontWeight: '600', marginBottom: '0.5rem' }}>
                      {bucket.label}
                    </div>
                    {bucket.invoices.map(inv => {
                      const customerName = inv.customers
                        ? (inv.customers.first_name && inv.customers.last_name
                            ? `${inv.customers.first_name} ${inv.customers.last_name}`
                            : inv.customers.name || 'Unknown')
                        : 'Unknown';
                      return (
                        <div key={inv.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '0.5rem 0.75rem',
                          background: colors.bg,
                          borderRadius: '6px',
                          marginBottom: '0.25rem',
                          fontSize: '0.85rem'
                        }}>
                          <div>
                            <span style={{ color: colors.text }}>{inv.invoice_number || 'No #'}</span>
                            <span style={{ color: colors.textMuted, marginLeft: '0.75rem' }}>{customerName}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>{inv.daysOld}d</span>
                            <span style={{ color: bucket.color, fontWeight: '600' }}>{formatCurrency(inv.balance)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ))}
            </div>
          ) : (
            <div style={{
              background: colors.bgCard,
              padding: '1.5rem',
              borderRadius: '12px',
              border: `1px solid ${colors.border}`,
              marginBottom: '2rem'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={20} color={colors.accent} />
                Aging Receivables
              </h3>
              <p style={{ color: colors.textMuted, fontSize: '0.9rem', margin: 0 }}>
                No outstanding invoices. All caught up!
              </p>
            </div>
          );
        })()}

        {/* Profit & Loss Summary */}
        {(() => {
          const now = new Date();
          const plMonths = [];
          for (let i = 5; i >= 0; i--) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

            const revenue = invoices
              .filter(inv => {
                if (inv.status !== 'paid') return false;
                const invDate = new Date(inv.created_at);
                return invDate >= date && invDate <= monthEnd;
              })
              .reduce((sum, inv) => sum + (parseFloat(inv.amount_paid) || parseFloat(inv.total) || 0), 0);

            const expenseTotal = expenses
              .filter(exp => {
                const expDate = exp.expense_date;
                return expDate >= monthKey + '-01' && expDate <= monthKey + '-31';
              })
              .reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

            plMonths.push({
              label: monthLabel,
              revenue,
              expenses: expenseTotal,
              profit: revenue - expenseTotal,
              isCurrent: i === 0
            });
          }

          const currentMonth = plMonths[plMonths.length - 1];
          const hasData = plMonths.some(m => m.revenue > 0 || m.expenses > 0);

          return (
            <div style={{
              background: colors.bgCard,
              padding: '1.5rem',
              borderRadius: '12px',
              border: `1px solid ${colors.border}`,
              marginBottom: '2rem'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <TrendingUp size={20} color={colors.accent} />
                Profit & Loss (6 Months)
              </h3>

              {/* Current month summary */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: isMobile ? '0.75rem' : '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ padding: '1rem', background: colors.bg, borderRadius: '8px', borderLeft: `4px solid ${FIN.positive}` }}>
                  <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Revenue</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: FIN.positive }}>{formatCurrency(currentMonth.revenue)}</div>
                </div>
                <div style={{ padding: '1rem', background: colors.bg, borderRadius: '8px', borderLeft: `4px solid ${FIN.negative}` }}>
                  <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Expenses</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: FIN.negative }}>{formatCurrency(currentMonth.expenses)}</div>
                </div>
                <div style={{ padding: '1rem', background: colors.bg, borderRadius: '8px', borderLeft: `4px solid ${currentMonth.profit >= 0 ? FIN.positive : FIN.negative}` }}>
                  <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Net Profit</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: currentMonth.profit >= 0 ? FIN.positive : FIN.negative }}>
                    {formatCurrency(currentMonth.profit)}
                  </div>
                </div>
              </div>

              {/* Monthly table */}
              {hasData ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.textMuted, fontWeight: '500' }}>Month</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.textMuted, fontWeight: '500' }}>Revenue</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.textMuted, fontWeight: '500' }}>Expenses</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.textMuted, fontWeight: '500' }}>Net Profit</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.textMuted, fontWeight: '500' }}>Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plMonths.map((m, idx) => {
                        const margin = m.revenue > 0 ? ((m.profit / m.revenue) * 100).toFixed(1) : '0.0';
                        return (
                          <tr key={idx} style={{
                            borderBottom: `1px solid ${colors.borderLight}`,
                            background: m.isCurrent ? colors.bgInput : 'transparent'
                          }}>
                            <td style={{ padding: '0.6rem 0.5rem', color: m.isCurrent ? colors.accent : colors.text, fontWeight: m.isCurrent ? '600' : '400' }}>
                              {m.label}{m.isCurrent ? ' *' : ''}
                            </td>
                            <td style={{ textAlign: 'right', padding: '0.6rem 0.5rem', color: FIN.positive }}>
                              {formatCurrency(m.revenue)}
                            </td>
                            <td style={{ textAlign: 'right', padding: '0.6rem 0.5rem', color: FIN.negative }}>
                              {formatCurrency(m.expenses)}
                            </td>
                            <td style={{ textAlign: 'right', padding: '0.6rem 0.5rem', color: m.profit >= 0 ? FIN.positive : FIN.negative, fontWeight: '600' }}>
                              {formatCurrency(m.profit)}
                            </td>
                            <td style={{ textAlign: 'right', padding: '0.6rem 0.5rem', color: colors.textMuted }}>
                              {margin}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: colors.textMuted, textAlign: 'center', padding: '1rem 0' }}>
                  Add expenses in the Money module to see P&L data
                </p>
              )}
            </div>
          );
        })()}

        {/* Charts Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
          gap: isMobile ? '1rem' : '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Revenue Trend */}
          <div style={{
            background: colors.bgCard,
            padding: '1.5rem',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BarChart3 size={20} color={colors.accent} />
              Revenue Trend (6 Months)
            </h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', height: '200px' }}>
              {metrics.revenueByMonth.map((data, idx) => {
                const maxRevenue = Math.max(...metrics.revenueByMonth.map(d => d.revenue), 1);
                const height = Math.max((data.revenue / maxRevenue) * 100, 5);
                return (
                  <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{
                      width: '100%',
                      height: `${height}%`,
                      background: data.isCurrent ? `linear-gradient(to top, ${colors.accent}, ${colors.accentHover})` : colors.bgHover,
                      borderRadius: '4px 4px 0 0',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      paddingTop: '0.5rem',
                      minHeight: '30px'
                    }}>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        color: data.isCurrent ? '#1a1a1a' : colors.text
                      }}>
                        {formatCurrency(data.revenue)}
                      </span>
                    </div>
                    <span style={{
                      fontSize: '0.7rem',
                      color: data.isCurrent ? colors.accent : colors.textMuted
                    }}>
                      {data.month}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Job Pipeline */}
          <div style={{
            background: colors.bgCard,
            padding: '1.5rem',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Briefcase size={20} color={colors.accent} />
              Job Pipeline
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[
                { label: 'Scheduled', count: metrics.jobs.scheduled, color: FIN.info },
                { label: 'In Progress', count: metrics.jobs.inProgress, color: FIN.warning },
                { label: 'Completed', count: metrics.jobs.completed, color: FIN.positive }
              ].map((item, idx) => (
                <div key={idx} style={{
                  padding: '1rem',
                  background: colors.bg,
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderLeft: `3px solid ${item.color}`
                }}>
                  <span style={{ color: colors.text }}>{item.label}</span>
                  <span style={{ fontSize: '1.5rem', fontWeight: '700', color: item.color }}>{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: isMobile ? '1rem' : '1.5rem'
        }}>
          {/* Revenue by Source */}
          <div style={{
            background: colors.bgCard,
            padding: '1.5rem',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <PieChart size={20} color={colors.accent} />
              Revenue by Lead Source
            </h3>
            {metrics.revenueBySource.length === 0 ? (
              <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>No data yet</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {metrics.revenueBySource.map((source, idx) => {
                  const chartColors = [colors.accent, FIN.positive, FIN.info, FIN.purple, FIN.warning];
                  const color = chartColors[idx % chartColors.length];
                  return (
                    <div key={idx} style={{ padding: '0.75rem', background: colors.bg, borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ color: colors.text, fontSize: '0.9rem' }}>{source.source}</span>
                        <span style={{ color: color, fontWeight: '600' }}>{formatCurrency(source.revenue)}</span>
                      </div>
                      <div style={{ width: '100%', height: '6px', background: colors.bgInput, borderRadius: '3px' }}>
                        <div style={{ width: `${source.percentage}%`, height: '100%', background: color, borderRadius: '3px' }} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                        {source.percentage.toFixed(1)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top Customers */}
          <div style={{
            background: colors.bgCard,
            padding: '1.5rem',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Users size={20} color={colors.accent} />
              Top Customers
            </h3>
            {metrics.topCustomers.length === 0 ? (
              <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>No data yet</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {metrics.topCustomers.map((customer, idx) => (
                  <div key={idx} style={{
                    padding: '1rem',
                    background: colors.bg,
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ color: colors.text, fontWeight: '500' }}>{customer.name}</div>
                      <div style={{ color: colors.textMuted, fontSize: '0.8rem' }}>{customer.jobs} job{customer.jobs !== 1 ? 's' : ''}</div>
                    </div>
                    <div style={{ color: FIN.positive, fontWeight: '700', fontSize: '1.1rem' }}>
                      {formatCurrency(customer.revenue)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Jobs by Type */}
          <div style={{
            background: colors.bgCard,
            padding: '1.5rem',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Briefcase size={20} color={colors.accent} />
              Jobs by Type
            </h3>
            {metrics.jobsByType.length === 0 ? (
              <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>No data yet</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {metrics.jobsByType.map((item, idx) => {
                  const typeColors = {
                    installation: FIN.info,
                    repair: FIN.warning,
                    maintenance: FIN.purple,
                    inspection: FIN.teal,
                    other: '#9CA3AF'
                  };
                  const color = typeColors[item.type] || '#9CA3AF';
                  return (
                    <div key={idx} style={{
                      padding: '1rem',
                      background: colors.bg,
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderLeft: `3px solid ${color}`
                    }}>
                      <span style={{ color: colors.text, textTransform: 'capitalize' }}>{item.type}</span>
                      <span style={{ color: color, fontWeight: '700', fontSize: '1.25rem' }}>{item.count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusable Metric Card Component
function MetricCard({ icon, label, value, subValue, change, badge, badgeColor, color, isMobile }) {
  const { colors } = useTheme();
  return (
    <div style={{
      background: colors.bgCard,
      padding: isMobile ? '1rem' : '1.5rem',
      borderRadius: '12px',
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${color}`,
      minWidth: 0  // allow text to truncate inside grid cell on small screens
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
        {icon}
        {change && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.25rem 0.5rem',
            background: change.direction === 'up' ? FIN.positive + '20' : change.direction === 'down' ? FIN.negative + '20' : `${colors.textMuted}20`,
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: change.direction === 'up' ? FIN.positive : change.direction === 'down' ? FIN.negative : colors.textMuted
          }}>
            {change.direction === 'up' ? <ArrowUp size={12} /> : change.direction === 'down' ? <ArrowDown size={12} /> : null}
            {change.percent}%
          </div>
        )}
      </div>
      <div style={{
        fontSize: isMobile ? '1.35rem' : '1.75rem',
        fontWeight: '700',
        color: color,
        marginBottom: '0.25rem',
        wordBreak: 'break-word'
      }}>
        {value}
      </div>
      <div style={{ fontSize: '0.75rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        {label}
      </div>
      {subValue && (
        <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginTop: '0.25rem' }}>{subValue}</div>
      )}
      {badge && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.25rem 0.5rem',
          background: `${badgeColor}20`,
          borderRadius: '4px',
          fontSize: '0.75rem',
          color: badgeColor,
          display: 'inline-block'
        }}>
          {badge}
        </div>
      )}
    </div>
  );
}

